import { Button } from "@bcc/components/ui/button";
import { useDebounce } from "@bcc/hooks/use-debounce";
import { cn } from "@bcc/lib/utils";
import type { PurchaseOrderStatus } from "@shared/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ORDER_STATUS_FILTERS } from "../constants";
import {
  setOrdersFilterBarContainerTestId,
  setOrdersFilterBarStatusButtonTestId,
  setOrdersFilterBarStatusCountSkeletonTestId,
  setOrdersFilterBarStatusCountTestId,
} from "./orders-filter-bar.utils";
import { OrdersSearchInput } from "./orders-search-input";

const SEARCH_DEBOUNCE_MS = 300;

interface OrdersFilterBarProps {
  /** Current URL ?orderStatus value (comma-separated), empty string = "all" */
  readonly activeStatuses: string;
  readonly onFilterChange: (statuses: readonly PurchaseOrderStatus[]) => void;
  readonly onSearch: (query: string) => void;
  readonly searchQuery?: string;
  readonly statusCounts?: Record<string, number | undefined>;
  readonly isLoading?: boolean;
  readonly testId?: string;
}

export function OrdersFilterBar({
  activeStatuses,
  onFilterChange,
  onSearch,
  searchQuery: initialSearch = "",
  statusCounts,
  isLoading = false,
  testId,
}: OrdersFilterBarProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const debouncedSearch = useDebounce(onSearch, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    onSearch(""); // immediate — no debounce
  };

  return (
    <div data-testid={testId ?? setOrdersFilterBarContainerTestId()} className="space-y-4">
      <div className="flex gap-2">
        <OrdersSearchInput value={searchQuery} onChange={handleSearchChange} onClear={handleClearSearch} />
      </div>

      <div className="flex flex-wrap gap-2">
        {ORDER_STATUS_FILTERS.map(({ key, labelKey, dotColor, statuses }) => {
          const isActive = activeStatuses === [...statuses].join(",");
          const count = statusCounts?.[key];
          return (
            <Button
              key={key}
              data-testid={setOrdersFilterBarStatusButtonTestId(key)}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => onFilterChange([...statuses])}
              className={cn("gap-1.5", isActive && "shadow-sm")}
            >
              <div className={cn("h-2 w-2 rounded-full", dotColor)} />
              {t(labelKey) as string}
              {isLoading && (
                <span
                  data-testid={setOrdersFilterBarStatusCountSkeletonTestId(key)}
                  className="ml-1 inline-block h-5 w-6 animate-pulse rounded-full bg-muted"
                  aria-hidden="true"
                />
              )}
              {!isLoading && count !== undefined && (
                <span
                  data-testid={setOrdersFilterBarStatusCountTestId(key)}
                  className={cn(
                    "ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-medium",
                    isActive ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"
                  )}
                >
                  {count}
                </span>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
